/**
 * F-508-FE-10: LanguageManagement
 *
 * /admin/languages
 * - Language list table: name, BCP-47, direction, STT model, TTS voice, status badge
 * - Add language: modal form with name, BCP-47, direction, STT model, TTS voice
 * - Edit language: modal (BCP-47 immutable after first session use — disabled field + tooltip)
 * - Activate/deactivate toggle (last active disabled + tooltip)
 * - Deactivation warning showing doctor/session counts
 *
 * API:
 * - GET /admin/languages
 * - POST /admin/languages
 * - PATCH /admin/languages/:id
 * - POST /admin/languages/:id/activate
 * - POST /admin/languages/:id/deactivate
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Language {
  id: string;
  name: string;
  code: string;         // BCP-47
  direction: 'LTR' | 'RTL';
  sttModel: string;
  ttsVoice: string;
  status: 'ACTIVE' | 'INACTIVE';
  isImmutable: boolean; // true after first session use
  activeDoctors?: number;
  activeSessions?: number;
}

interface LanguageFormData {
  name: string;
  code: string;
  direction: 'LTR' | 'RTL';
  sttModel: string;
  ttsVoice: string;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'ACTIVE' | 'INACTIVE' }) {
  if (status === 'ACTIVE') {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">Active</span>;
  }
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">Inactive</span>;
}

// ---------------------------------------------------------------------------
// Direction badge
// ---------------------------------------------------------------------------

function DirectionBadge({ direction }: { direction: 'LTR' | 'RTL' }) {
  return (
    <span className="font-mono text-xs text-clinical-text-muted bg-clinical-cream-dark px-2 py-0.5 rounded">
      {direction}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Deactivate warning dialog
// ---------------------------------------------------------------------------

interface DeactivateDialogProps {
  language: Language | null;
  onConfirm: (id: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeactivateDialog({ language, onConfirm, onCancel, isLoading }: DeactivateDialogProps) {
  if (!language) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-3">
          Deactivate {language.name}?
        </h3>
        <div className="space-y-2 text-sm font-body text-clinical-text-muted mb-6">
          {(language.activeDoctors ?? 0) > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-clinical border border-amber-200">
              <svg className="w-4 h-4 text-amber-500 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span>
                <strong>{language.activeDoctors}</strong> doctor{language.activeDoctors !== 1 ? 's' : ''} and{' '}
                <strong>{language.activeSessions ?? 0}</strong> active session{(language.activeSessions ?? 0) !== 1 ? 's' : ''} use this language.
              </span>
            </div>
          )}
          <p>Deactivating will prevent new sessions in this language. Existing sessions may be affected.</p>
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="btn-secondary px-4 py-2 text-sm min-h-[36px]" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(language.id)}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm min-h-[36px] bg-red-600 text-white font-body font-medium rounded-clinical hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Language form modal (add/edit)
// ---------------------------------------------------------------------------

interface LanguageFormModalProps {
  open: boolean;
  language: Language | null; // null = add mode
  onClose: () => void;
  onSuccess: () => void;
}

function LanguageFormModal({ open, language, onClose, onSuccess }: LanguageFormModalProps) {
  const isEdit = language !== null;

  const [form, setForm] = useState<LanguageFormData>({
    name: language?.name ?? '',
    code: language?.code ?? '',
    direction: language?.direction ?? 'LTR',
    sttModel: language?.sttModel ?? '',
    ttsVoice: language?.ttsVoice ?? '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: keyof LanguageFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.code.trim()) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (isEdit && language) {
        await api.patch(`/admin/languages/${language.id}`, {
          name: form.name.trim(),
          direction: form.direction,
          sttModel: form.sttModel.trim(),
          ttsVoice: form.ttsVoice.trim(),
        });
      } else {
        await api.post('/admin/languages', {
          name: form.name.trim(),
          code: form.code.trim(),
          direction: form.direction,
          sttModel: form.sttModel.trim(),
          ttsVoice: form.ttsVoice.trim(),
        });
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save language.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-5">
          {isEdit ? `Edit ${language.name}` : 'Add Language'}
        </h3>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          <div>
            <label htmlFor="lang-name" className="form-label">Language Name</label>
            <input
              id="lang-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="form-input"
              placeholder="e.g. Arabic"
              required
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="lang-code" className="form-label">
              BCP-47 Code
              {isEdit && language?.isImmutable && (
                <span className="ms-2 text-xs font-normal text-clinical-text-muted">(locked after first session use)</span>
              )}
            </label>
            <div className="relative">
              <input
                id="lang-code"
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                className={`form-input font-mono ${isEdit && language?.isImmutable ? 'bg-clinical-cream-dark text-clinical-text-muted cursor-not-allowed' : ''}`}
                placeholder="e.g. ar-SA"
                required
                disabled={isEdit && language?.isImmutable}
                title={isEdit && language?.isImmutable ? 'BCP-47 code cannot be changed after the language has been used in a session.' : undefined}
              />
            </div>
            {isEdit && language?.isImmutable && (
              <p className="text-xs font-body text-clinical-text-muted mt-1">
                BCP-47 code is immutable after first session use to prevent data integrity issues.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="lang-direction" className="form-label">Text Direction</label>
            <select
              id="lang-direction"
              value={form.direction}
              onChange={(e) => handleChange('direction', e.target.value as 'LTR' | 'RTL')}
              className="form-input"
            >
              <option value="LTR">LTR — Left to Right</option>
              <option value="RTL">RTL — Right to Left</option>
            </select>
          </div>

          <div>
            <label htmlFor="lang-stt" className="form-label">STT Model</label>
            <input
              id="lang-stt"
              type="text"
              value={form.sttModel}
              onChange={(e) => handleChange('sttModel', e.target.value)}
              className="form-input font-mono"
              placeholder="e.g. latest_long"
            />
          </div>

          <div>
            <label htmlFor="lang-tts" className="form-label">TTS Voice</label>
            <input
              id="lang-tts"
              type="text"
              value={form.ttsVoice}
              onChange={(e) => handleChange('ttsVoice', e.target.value)}
              className="form-input font-mono"
              placeholder="e.g. ar-XA-Wavenet-A"
            />
          </div>

          {error && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm font-body text-red-700">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm min-h-[36px]">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary px-4 py-2 text-sm min-h-[36px]">
              {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Language'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LanguageManagement() {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editLanguage, setEditLanguage] = useState<Language | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Language | null>(null);

  const { data: languages, isLoading } = useQuery<Language[]>({
    queryKey: ['admin', 'languages'],
    queryFn: async () => {
      const res = await api.get<{ data: Language[]; count: number }>('/admin/languages');
      return res.data;
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'languages'] });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/languages/${id}/activate`),
    onSuccess: invalidate,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/languages/${id}/deactivate`),
    onSuccess: () => {
      invalidate();
      setDeactivateTarget(null);
    },
  });

  const activeLanguages = languages?.filter((l) => l.status === 'ACTIVE') ?? [];
  const isLastActive = activeLanguages.length === 1;

  return (
    <AdminLayout breadcrumbs={[{ label: 'Languages' }]}>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">Languages</h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Manage platform languages for STT, translation, and TTS.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditLanguage(null); setShowModal(true); }}
          className="btn-primary text-sm px-4 py-2 min-h-[40px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Language
        </button>
      </div>

      {/* Table */}
      <div className="clinical-card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-12 w-full rounded" />
            ))}
          </div>
        ) : (languages?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-body text-clinical-text-muted">No languages configured yet.</p>
            <button
              type="button"
              onClick={() => { setEditLanguage(null); setShowModal(true); }}
              className="btn-primary text-sm px-4 py-2 min-h-[40px] mt-4"
            >
              Add First Language
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="border-b border-clinical-border bg-clinical-cream">
                  <th className="text-start px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Name</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">BCP-47</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Direction</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide hidden lg:table-cell">STT Model</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide hidden lg:table-cell">TTS Voice</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Status</th>
                  <th className="text-end px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-border">
                {languages?.map((lang, idx) => {
                  const isOnlyActive = lang.status === 'ACTIVE' && isLastActive;
                  return (
                    <tr key={lang.id} className={`hover:bg-clinical-cream/50 transition-colors ${idx % 2 === 0 ? '' : 'bg-clinical-cream/30'}`}>
                      <td className="px-5 py-3 font-medium text-clinical-charcoal">{lang.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-clinical-text-muted">{lang.code}</td>
                      <td className="px-4 py-3"><DirectionBadge direction={lang.direction} /></td>
                      <td className="px-4 py-3 font-mono text-xs text-clinical-text-muted hidden lg:table-cell">{lang.sttModel || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-clinical-text-muted hidden lg:table-cell">{lang.ttsVoice || '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={lang.status} /></td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => { setEditLanguage(lang); setShowModal(true); }}
                            className="text-xs text-clinical-text-muted font-medium hover:text-clinical-charcoal"
                          >
                            Edit
                          </button>
                          <span className="text-clinical-border">|</span>
                          {lang.status === 'ACTIVE' ? (
                            <span title={isOnlyActive ? 'Cannot deactivate the last active language.' : undefined}>
                              <button
                                type="button"
                                onClick={() => !isOnlyActive && setDeactivateTarget(lang)}
                                disabled={isOnlyActive || deactivateMutation.isPending}
                                className="text-xs text-red-500 font-medium hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Deactivate
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => activateMutation.mutate(lang.id)}
                              disabled={activateMutation.isPending}
                              className="text-xs text-clinical-teal font-medium hover:underline"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      <LanguageFormModal
        open={showModal}
        language={editLanguage}
        onClose={() => setShowModal(false)}
        onSuccess={invalidate}
      />

      {/* Deactivate warning */}
      <DeactivateDialog
        language={deactivateTarget}
        onConfirm={(id) => deactivateMutation.mutate(id)}
        onCancel={() => setDeactivateTarget(null)}
        isLoading={deactivateMutation.isPending}
      />
    </AdminLayout>
  );
}
